import React from "react";
import { styled } from "@mui/material/styles";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import Input from "../../../component-lib/Atoms/Input/Input";
import Button from "../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import { VALIDATION_CONSTANTS } from "./ValidationConstants";
import { useDispatch, useSelector } from "react-redux";

import EventEHRLazyDropdown from "../../../component-lib/Molecules/lazy-loading-search/EventEHRLazyLoadingSearch";
import moment from "moment";
import DatePicker from "../../../component-lib/Atoms/DatePicker/DatePicker";
import {
  hideLoader,
  showLoader,
} from "../../../component-lib/Molecules/loader/LoaderSlice";
import {
  getLazyLoadingApiResponse,
  getLazyLoadingSearchApiResponse,
} from "../../../utils/constantFunctions";
import { componentKey } from "../InventorySlice";
import { REQUESt_INVENTORY_DATA } from "./requestInventoryConstants";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "0px 24px 16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    BorderAllRounded: "8px",
    Height: "500px",
    width: "708px", // Set your custom width here
    maxWidth: "none", // Prevents Material-UI from overriding your width
  },
}));

interface RequestInventoryDialogProps {
  defaultInventoryData?: any;
  isOpen: boolean;
  dialogHeading: string;
  handleData: (data: any) => void;
  handleClose: () => void;
  // check: boolean;
}

export default function RequestInventoryDialog({
  defaultInventoryData,
  // check,
  isOpen = false,
  dialogHeading = "Your Heading Here...",
  handleClose,
  handleData,
}: RequestInventoryDialogProps) {
  const dispatch = useDispatch();

  const { inventoryData } = useSelector((state: any) => state[componentKey]);
  // GET INVENTORY LOGIC START: THIS USE STATES FOR GET INVENTORY--------------
  const [pageCountForInventory, setPageCountForInventoryList] =
    React.useState(1);
  const [dropdownSearchForInventory, setDropdownSearchForInventory] =
    React.useState("");
  const [dropdownOptionsForInventory, setDropdownOptionsForInventory] =
    React.useState<any>([]);
  //console.log("dropdownOptionsForInventory", dropdownOptionsForInventory);

  const [selectedValueForInventory, setSelectedValueForInventory] =
    React.useState<any>(null);
  const [resetFlagForInventory, setResetFlagForInventory] =
    React.useState(false);

  //GET INVENTORY LOGIC START: END **************************************************************

  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);

  // INVENTORY LOGIC START: Below useEffect and functions are used for getting inventory list, searching inventory, and lazy loading inventory data

  React.useEffect(() => {
    if (!resetFlagForInventory) {
      if (dropdownSearchForInventory && dropdownSearchForInventory.length > 0) {
        getDropdownSearchInventoryList(dropdownSearchForInventory);
      } else {
        getDropdownInventoryList(pageCountForInventory);
      }
    }
  }, [dropdownSearchForInventory, pageCountForInventory]);

  React.useEffect(() => {
    if (resetFlagForInventory) {
      resetInventoryList();
    }
  }, [resetFlagForInventory]);

  // Reset inventory list and dropdown state
  const resetInventoryList = () => {
    setSelectedValueForInventory(null);
    setDropdownSearchForInventory("");
    setPageCountForInventoryList(1);
    getDropdownInventoryList(1, true);
  };

  // Handle search input with debounce
  const handleInventorySearch = (searchTerm: string) => {
    if (timer) {
      clearTimeout(timer); // Clear the previous timer
    }

    // Set a new timer with a 500ms delay for the search term
    const newTimer = setTimeout(() => {
      setDropdownSearchForInventory(searchTerm);
    }, 500);

    setTimer(newTimer); // Store the timer ID for cleanup
  };

  // Get inventory list for lazy loading
  const getDropdownInventoryList = async (pageCount: number, reset = false) => {
    dispatch(showLoader());
    try {
      const response = await getLazyLoadingApiResponse(
        "admin/inventory",
        pageCount,
        6,
      );

      let inventoryList = response?.data?.invetories || {};

      const mappedInventoryData = inventoryList?.map(
        (data: any, index: number) => ({
          label: `${data?.itemName}`,
          value: `${data?.id}`,
          key: `${data?.id}-${index}`, // Add the index as a fallback to ensure uniqueness
          skuCode: data?.skuCode,
          type: data?.type,
        }),
      );

      if (mappedInventoryData && mappedInventoryData?.length > 0) {
        if (reset) {
          setDropdownOptionsForInventory(mappedInventoryData); // Reset the dropdown options
          setResetFlagForInventory(false);
        } else if (
          !dropdownOptionsForInventory.includes(mappedInventoryData[0])
        ) {
          let options = dropdownOptionsForInventory.concat(mappedInventoryData);
          setDropdownOptionsForInventory(options); // Append new inventory data
        }
      }
    } catch (error) {
      console.log("Error fetching inventory list:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  // Get inventory list based on search term
  const getDropdownSearchInventoryList = async (search: string) => {
    try {
      const response = await getLazyLoadingSearchApiResponse(
        "admin/inventory",
        search,
        "searchValue",
      );

      let inventoryList = response?.data?.invetories || {};

      const mappedInventoryData = inventoryList?.map((data: any) => ({
        label: `${data?.itemName}`, // Use the inventory name as the label
        value: `${data?.id}`, // Use the inventory ID as the value
        skuCode: data?.skuCode,
        type: data?.type,
      }));

      if (mappedInventoryData?.length) {
        setDropdownOptionsForInventory(mappedInventoryData); // Update options with search results
      }
    } catch (error) {
      console.log("Error fetching inventory search list:", error);
    }
  };

  // **************************************************************

  const validationSchema = Yup.object().shape({
    [REQUESt_INVENTORY_DATA?.ITEM_NAME]: Yup.string().required(
      "Item name is required",
    ),
    [REQUESt_INVENTORY_DATA?.SKU_CODE]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.NAME,
        "SKU code can only contain alphanumeric characters",
      )
      .required("SKU code is required"),
    [REQUESt_INVENTORY_DATA?.CATEGORY]: Yup.string().required(
      "Category is required",
    ),
    [REQUESt_INVENTORY_DATA?.EXPIRATION_DATE]: Yup.string().required(
      "Expiration Date is required",
    ),
    [REQUESt_INVENTORY_DATA?.QUANTITY]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.ID,
        "Quantity must contain only letters and numbers",
      )
      .required("Quantity is required"),

    [REQUESt_INVENTORY_DATA.THRESHOLD_VALUE]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.ID,
        "Threshold value must contain only letters and numbers",
      )
      .required("Threshold value is required")
      .matches(
        VALIDATION_CONSTANTS.LIMIT_10,
        "Threshold value should not greater than 10 digits.",
      )
      .test(
        "threshold-not-greater-than-quantity",
        "Threshold value must not be greater than quantity",
        function (value) {
          const { quantity } = this.parent;
          return Number(value) <= Number(quantity); // Ensure threshold value is <= quantity
        },
      ),
  });
  React.useEffect(() => {
    if (defaultInventoryData?.id) {
      setSelectedValueForInventory({
        label: defaultInventoryData?.itemName,
        value: defaultInventoryData?.id,
      });
    }
  }, [defaultInventoryData]);

  const isEditMode = !!defaultInventoryData?.id;

  //ORDER STATUS SHOULD BE DELIVER THAN SHOW SAVE BUTTON
  const checkForReOrder = defaultInventoryData?.orderStatus === "Delivered";

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [REQUESt_INVENTORY_DATA?.ITEM_NAME]: `${
            isEditMode ? defaultInventoryData?.itemName || "" : ""
          }`,
          [REQUESt_INVENTORY_DATA?.SKU_CODE]: `${
            isEditMode ? defaultInventoryData?.skuCode || "" : ""
          }`,
          [REQUESt_INVENTORY_DATA.CATEGORY]: `${
            isEditMode ? defaultInventoryData?.category || "" : ""
          }`,
          [REQUESt_INVENTORY_DATA?.EXPIRATION_DATE]:
            defaultInventoryData?.expirationDate
              ? // Date-only prefill (midnight UTC) — read in UTC so the picker
                // shows the correct calendar date.
                moment.utc(defaultInventoryData?.expirationDate).format(
                  "MM/DD/YYYY",
                )
              : "08/01/2026",
          [REQUESt_INVENTORY_DATA?.QUANTITY]: `${
            isEditMode ? defaultInventoryData?.order || "" : ""
          }`,
          [REQUESt_INVENTORY_DATA?.THRESHOLD_VALUE]: `${
            isEditMode ? defaultInventoryData?.reorderPoint || "" : ""
          }`,
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            // getFormData(values);
            handleData(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false); // Reset submitting state
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          isValid,
          dirty,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                {dialogHeading}{" "}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              {/* MAIN CONTENT */}
              <DialogContent>
                <div
                  style={{
                    // height: deviceType === "mobile" ? "40vh" : "fit-content",
                    overflow: "auto",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <div className="pt-4">
                    <EventEHRLazyDropdown
                      label="Item Name"
                      onBlur={handleBlur}
                      dropdownIcon={true}
                      isDisabled={isEditMode} // Disable if editing
                      error={
                        touched[REQUESt_INVENTORY_DATA.ITEM_NAME] &&
                        Boolean(errors[REQUESt_INVENTORY_DATA.ITEM_NAME])
                      }
                      helperText={
                        touched[REQUESt_INVENTORY_DATA.ITEM_NAME]
                          ? errors[REQUESt_INVENTORY_DATA.ITEM_NAME]
                          : undefined
                      }
                      dropdownOptions={dropdownOptionsForInventory} // Dynamically loaded inventory options
                      dropdownSearch={dropdownSearchForInventory} // Search logic for inventory
                      selectedValue={selectedValueForInventory} // Formik-controlled value
                      setDropdownSearch={(searchTerm) => {
                        handleInventorySearch(searchTerm); // Fetch inventory data based on search input
                      }}
                      setPageCount={(data) => {
                        setPageCountForInventoryList(data); // Update the page count for inventory list
                      }}
                      setResetFlag={(reset) => {
                        setResetFlagForInventory(reset); // Reset dropdown state if needed
                      }}
                      setSelectedValue={(data) => {
                        const selectedValue = data?.value || "";

                        // Update the selected item name in Formik values
                        setFieldValue(
                          REQUESt_INVENTORY_DATA.ITEM_NAME,
                          selectedValue,
                        );

                        //Find and update related fields dynamically
                        const selectedInventoryData =
                          dropdownOptionsForInventory?.find(
                            (data: any) => data?.value === selectedValue,
                          );

                        if (selectedInventoryData) {
                          setFieldValue(
                            REQUESt_INVENTORY_DATA.SKU_CODE,
                            selectedInventoryData.skuCode,
                          );
                          setFieldValue(
                            REQUESt_INVENTORY_DATA.CATEGORY,
                            selectedInventoryData.type,
                          );
                        } else {
                          setFieldValue(REQUESt_INVENTORY_DATA.SKU_CODE, "");
                          setFieldValue(REQUESt_INVENTORY_DATA.CATEGORY, "");
                        }

                        //Update the selected value state for the dropdown
                        setSelectedValueForInventory(data);
                      }}
                    />
                  </div>
                  <div className="pt-4">
                    <Input
                      textFieldLabel="SKU Code"
                      disabled={true}
                      size="small"
                      name={REQUESt_INVENTORY_DATA.SKU_CODE}
                      value={values[REQUESt_INVENTORY_DATA.SKU_CODE]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          REQUESt_INVENTORY_DATA.SKU_CODE,
                          e.target.value,
                        );
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[REQUESt_INVENTORY_DATA.SKU_CODE] &&
                        Boolean(errors[REQUESt_INVENTORY_DATA.SKU_CODE])
                      }
                      helperText={
                        touched[REQUESt_INVENTORY_DATA.SKU_CODE]
                          ? errors[REQUESt_INVENTORY_DATA.SKU_CODE]
                          : undefined
                      }
                    />
                  </div>
                  <div>
                    <Input
                      textFieldLabel="Type"
                      disabled={true}
                      size="small"
                      name={REQUESt_INVENTORY_DATA.CATEGORY}
                      value={values[REQUESt_INVENTORY_DATA.CATEGORY]}
                      onChangeCb={(e) =>
                        setFieldValue(
                          REQUESt_INVENTORY_DATA.CATEGORY,
                          e.target.value,
                        )
                      }
                      error={
                        touched[REQUESt_INVENTORY_DATA.CATEGORY] &&
                        Boolean(errors[REQUESt_INVENTORY_DATA.CATEGORY])
                      }
                      helperText={
                        touched[REQUESt_INVENTORY_DATA.CATEGORY]
                          ? errors[REQUESt_INVENTORY_DATA.CATEGORY]
                          : undefined
                      }
                    />
                  </div>
                  <DatePicker
                    name={REQUESt_INVENTORY_DATA.EXPIRATION_DATE}
                    value={
                      values[REQUESt_INVENTORY_DATA.EXPIRATION_DATE]
                        ? moment(values[REQUESt_INVENTORY_DATA.EXPIRATION_DATE]) // Convert to a moment object
                        : null
                    }
                    onChangeCb={(date) => {
                      setFieldValue(
                        REQUESt_INVENTORY_DATA.EXPIRATION_DATE,
                        date ? moment(date).toISOString() : "", // Store ISO string in Formik
                      );
                    }}
                    label="Expiration Date"
                    style={{ width: "100%" }}
                    minDate={moment().startOf("day")} // Ensure minDate is a valid moment object
                    error={
                      touched[REQUESt_INVENTORY_DATA.EXPIRATION_DATE] &&
                      Boolean(errors[REQUESt_INVENTORY_DATA.EXPIRATION_DATE])
                    }
                    helperText={
                      touched[REQUESt_INVENTORY_DATA.EXPIRATION_DATE]
                        ? errors[REQUESt_INVENTORY_DATA.EXPIRATION_DATE]
                        : undefined
                    }
                  />
                  <Input
                    textFieldLabel="Quantity"
                    // placeholder=""
                    size="small"
                    name={REQUESt_INVENTORY_DATA.QUANTITY}
                    value={values[REQUESt_INVENTORY_DATA.QUANTITY]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        REQUESt_INVENTORY_DATA.QUANTITY,
                        e.target.value,
                      );
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[REQUESt_INVENTORY_DATA.QUANTITY] &&
                      Boolean(errors[REQUESt_INVENTORY_DATA.QUANTITY])
                    }
                    helperText={
                      touched[REQUESt_INVENTORY_DATA.QUANTITY]
                        ? errors[REQUESt_INVENTORY_DATA.QUANTITY]
                        : undefined
                    }
                  />
                  {/*THRESHOLD VALUES*/}
                  <Input
                    // disabled
                    textFieldLabel="Threshold Value"
                    name={REQUESt_INVENTORY_DATA.THRESHOLD_VALUE}
                    value={values[REQUESt_INVENTORY_DATA.THRESHOLD_VALUE]}
                    type="number"
                    onChangeCb={(e) => {
                      setFieldValue(
                        REQUESt_INVENTORY_DATA.THRESHOLD_VALUE,
                        e.target.value,
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[REQUESt_INVENTORY_DATA.THRESHOLD_VALUE] &&
                      Boolean(errors[REQUESt_INVENTORY_DATA.THRESHOLD_VALUE])
                    }
                    helperText={
                      touched[REQUESt_INVENTORY_DATA.THRESHOLD_VALUE]
                        ? errors[REQUESt_INVENTORY_DATA.THRESHOLD_VALUE]
                        : undefined
                    }
                  />
                </div>
              </DialogContent>

              {/* ACTION BUTTON */}
              <DialogActions style={{ padding: "24px" }}>
                <Button
                  type="submit"
                  // disabled={check}
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    width: "200px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                ></Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
